import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Play, 
  Trash2, 
  Clock, 
  Globe, 
  GraduationCap, 
  Search, 
  Sparkles, 
  Film, 
  BookOpen,
  SearchX,
  Lock,
  Download,
  Loader2,
  AlertCircle,
  Youtube
} from 'lucide-react';
import { AIOutput } from '../lib/gemini';
import { cn } from '../lib/utils';

export interface SavedVideo {
  id: string;
  title: string;
  question: string;
  subject: string;
  topic: string;
  language: string;
  timestamp: number;
  videoData: AIOutput;
  imageUrl?: string;
}

// Helper to trigger official YouTube channel owner auth flow
export const connectOfficialYouTube = async (onSuccess?: () => void) => {
  try {
    const resp = await fetch(`/api/youtube/auth-url?origin=${encodeURIComponent(window.location.origin)}`);
    if (!resp.ok) {
      const err = await resp.json();
      throw new Error(err.error || "Failed to get YouTube authorization URL");
    }
    const { url } = await resp.json();
    const width = 550;
    const height = 650;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    const popup = window.open(url, 'youtube-owner-auth', `width=${width},height=${height},left=${left},top=${top}`);

    const handleAuthMsg = (event: MessageEvent) => {
      if (event.data?.type === 'YOUTUBE_AUTH_SUCCESS') {
        window.removeEventListener('message', handleAuthMsg);
        if (onSuccess) onSuccess();
      } else if (event.data?.type === 'YOUTUBE_AUTH_ERROR') {
        window.removeEventListener('message', handleAuthMsg);
        alert(event.data.error || 'Authorization failed');
      }
    };
    window.addEventListener('message', handleAuthMsg);
  } catch (err: any) {
    console.error("Failed to connect official YouTube:", err);
  }
};

interface MyVideosProps {
  userEmail: string | null;
  onReplayVideo: (data: AIOutput, language: string) => void;
  onLoginClick?: () => void;
}

export function MyVideos({ userEmail, onReplayVideo, onLoginClick }: MyVideosProps) {
  const [videos, setVideos] = useState<SavedVideo[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('All');
  const [isLoading, setIsLoading] = useState(true);
  const [isYtConnected, setIsYtConnected] = useState(false);

  useEffect(() => {
    if (userEmail) {
      fetch(`/api/youtube/connected?email=${encodeURIComponent(userEmail)}`)
        .then(res => {
          if (!res.ok) return null;
          return res.json();
        })
        .then(data => {
          if (data) setIsYtConnected(!!data.connected);
        })
        .catch(err => console.error("Error checking yt connection status:", err));
    } else {
      setIsYtConnected(false);
    }
  }, [userEmail]);

  // Load videos from localStorage
  const loadVideos = () => {
    setIsLoading(true);
    try {
      if (!userEmail) {
        setVideos([]);
        setIsLoading(false);
        return;
      }
      const storageKey = `profx_saved_videos_${userEmail}`;
      const savedRaw = localStorage.getItem(storageKey);
      if (savedRaw) {
        const parsed: SavedVideo[] = JSON.parse(savedRaw);
        // Sort by newest first
        parsed.sort((a, b) => b.timestamp - a.timestamp);
        setVideos(parsed);
      } else {
        setVideos([]);
      }
    } catch (err) {
      console.error("Failed to parse saved videos:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadVideos();

    // Listen to video list updates
    const handleUpdate = () => {
      loadVideos();
    };
    window.addEventListener('profx_videos_updated', handleUpdate);
    return () => {
      window.removeEventListener('profx_videos_updated', handleUpdate);
    };
  }, [userEmail]);

  // Delete a video
  const handleDeleteVideo = (e: React.MouseEvent, id: string) => {
    e.stopPropagation(); // Avoid triggering play
    if (!userEmail) return;
    try {
      const storageKey = `profx_saved_videos_${userEmail}`;
      const updated = videos.filter(v => v.id !== id);
      localStorage.setItem(storageKey, JSON.stringify(updated));
      setVideos(updated);
    } catch (err) {
      console.error("Failed to delete video:", err);
    }
  };

  // Get unique subjects for filter tabs
  const subjects = ['All', ...Array.from(new Set(videos.map(v => v.subject))).filter(Boolean)];

  // Filter and search videos
  const filteredVideos = videos.filter(video => {
    const matchesSearch = 
      video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      video.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      video.topic.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesSubject = selectedSubject === 'All' || video.subject === selectedSubject;
    
    return matchesSearch && matchesSubject;
  });

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    }) + ' ' + date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Generate an elegant, customizable gradient placeholder depending on the subject name
  const getRandomGradient = (subj: string) => {
    const s = (subj || 'math').toLowerCase();
    if (s.includes('math')) return 'from-indigo-950 via-slate-900 to-indigo-900';
    if (s.includes('phys')) return 'from-purple-950 via-slate-900 to-purple-900';
    if (s.includes('chem')) return 'from-emerald-950 via-slate-900 to-emerald-900';
    if (s.includes('code') || s.includes('prog')) return 'from-blue-950 via-slate-900 to-blue-900';
    return 'from-slate-900 via-indigo-950/20 to-slate-900';
  };

  return (
    <div id="my-videos-section" className="w-full max-w-6xl mx-auto px-6 mt-16 scroll-mt-24">
      {/* Section Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-semibold tracking-wide mb-3">
            <Film className="w-3.5 h-3.5 text-indigo-400" /> Dynamic Tutorial History
          </div>
          <h2 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white to-indigo-200">
            My Generated Videos
          </h2>
          <p className="text-slate-400 mt-1 max-w-xl text-sm leading-relaxed">
            Replay previously generated stepwise visual tutorials without spending questions limit or re-calculating solutions.
          </p>
        </div>

        {userEmail && videos.length > 0 && (
          <div className="relative w-full md:w-80">
            <input 
              type="text"
              placeholder="Search concepts or subjects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-950/60 border border-white/10 rounded-2xl px-11 py-3 text-slate-200 text-sm placeholder-slate-500 focus:outline-none focus:border-indigo-500/50 transition-colors"
            />
            <Search className="w-4 h-4 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
          </div>
        )}
      </div>



      {/* Guest Mode Overlay / Login CTA */}
      {!userEmail ? (
        <div className="bg-gradient-to-br from-slate-950 via-indigo-950/30 to-purple-950/20 border border-white/5 rounded-[2.5rem] p-8 md:p-12 text-center relative overflow-hidden backdrop-blur-sm shadow-2xl">
          <div className="absolute top-0 right-0 w-[350px] h-[350px] bg-indigo-600/5 rounded-full blur-[90px] pointer-events-none -translate-y-1/3 translate-x-1/3"></div>
          <div className="absolute bottom-0 left-0 w-[200px] h-[200px] bg-purple-500/5 rounded-full blur-[80px] pointer-events-none translate-y-1/3 -translate-x-1/3"></div>
          
          <div className="relative z-10 max-w-lg mx-auto flex flex-col items-center">
            <div className="w-16 h-16 rounded-3xl bg-indigo-950/80 border border-indigo-500/20 flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(79,70,229,0.15)] glow-indigo">
              <Lock className="w-7 h-7 text-indigo-400" />
            </div>
            <h3 className="text-2xl font-extrabold text-white mb-3">Unlock Private Video Dashboard</h3>
            <p className="text-slate-400 text-sm leading-relaxed mb-8">
              Sign in with Google to automatically back up every stepwise explanation video to your account, re-watch anytime, and build a personalized visual study vault.
            </p>
            {onLoginClick && (
              <button 
                onClick={onLoginClick}
                className="px-6 py-3.5 bg-indigo-600 hover:bg-indigo-505 text-white bg-gradient-to-r from-indigo-600 to-indigo-500 hover:scale-[1.03] active:scale-[0.98] rounded-xl font-bold text-sm shadow-[0_0_20px_rgba(79,70,229,0.3)] transition-all flex items-center gap-2 cursor-pointer"
              >
                <Globe className="w-4 h-4" /> Connect with Google Account
              </button>
            )}
          </div>
        </div>
      ) : isLoading ? (
        <div className="flex flex-col items-center justify-center p-20 border border-white/5 rounded-3xl bg-slate-900/10">
          <div className="w-10 h-10 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin mb-4"></div>
          <p className="text-slate-400 text-sm">Accessing your library securely...</p>
        </div>
      ) : videos.length === 0 ? (
        /* Empty State */
        <div className="bg-slate-900/20 border border-white/5 rounded-[2rem] p-12 text-center flex flex-col items-center justify-center">
          <div className="w-14 h-14 bg-indigo-500/5 rounded-2xl flex items-center justify-center border border-indigo-500/10 mb-5">
            <Film className="w-6 h-6 text-indigo-400/70" />
          </div>
          <h3 className="text-lg font-bold text-slate-200">No Visual Explanations Yet</h3>
          <p className="text-slate-400 text-sm max-w-sm mt-2 mb-6">
            Input a concept or upload a document/numerical question above, and Professor X will automatically save the step-by-step video response here!
          </p>
          <a 
            href="#input-section"
            className="text-xs font-semibold text-indigo-400 hover:text-indigo-300 flex items-center gap-1.5 transition-colors group"
          >
            Ask your first doubt <Sparkles className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
          </a>
        </div>
      ) : (
        /* Interactive Library view */
        <div className="space-y-6">
          {/* Subject Filter Tabs */}
          {subjects.length > 2 && (
            <div className="flex flex-wrap gap-2 items-center">
              {subjects.map(subject => (
                <button
                  key={subject}
                  onClick={() => setSelectedSubject(subject)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer ${
                    selectedSubject === subject
                      ? 'bg-indigo-500/20 border-indigo-500/40 text-indigo-300 shadow-[0_0_12px_rgba(79,70,229,0.1)]'
                      : 'bg-slate-900/40 border-white/5 text-slate-400 hover:text-slate-200 hover:border-white/10'
                  }`}
                >
                  {subject}
                </button>
              ))}
            </div>
          )}

          {/* Grid Layout of Cards */}
          <AnimatePresence mode="popLayout">
            {filteredVideos.length === 0 ? (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex flex-col items-center justify-center py-16 text-center"
              >
                <SearchX className="w-10 h-10 text-slate-500 mb-3" />
                <h4 className="text-slate-300 font-bold">No results found</h4>
                <p className="text-slate-500 text-xs mt-1">Try refining your search keyword or selecting "All" subjects.</p>
              </motion.div>
            ) : (
              <motion.div 
                layout
                className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
              >
                {filteredVideos.map(video => (
                  <VideoCard
                    key={video.id}
                    video={video}
                    userEmail={userEmail}
                    onReplayVideo={onReplayVideo}
                    handleDeleteVideo={handleDeleteVideo}
                    getRandomGradient={getRandomGradient}
                    formatDate={formatDate}
                    onLoginClick={onLoginClick}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

// Stateful inner component representing a single interactive video card with background compilation polling
interface VideoCardProps {
  key?: string;
  video: SavedVideo;
  userEmail: string | null;
  onReplayVideo: (data: AIOutput, language: string) => void;
  handleDeleteVideo: (e: React.MouseEvent, id: string) => void;
  getRandomGradient: (subj: string) => string;
  formatDate: (ts: number) => string;
  onLoginClick?: () => void;
}

function VideoCard({
  video,
  userEmail,
  onReplayVideo,
  handleDeleteVideo,
  getRandomGradient,
  formatDate,
  onLoginClick
}: VideoCardProps) {
  const [renderStatus, setRenderStatus] = useState<any>(null);
  const [ytStatus, setYtStatus] = useState<any>(null);
  const [isQueueing, setIsQueueing] = useState(false);

  const fetchStatus = () => {
    fetch(`/api/render/status/${video.id}`)
      .then((res) => {
        if (!res.ok) return null;
        return res.json();
      })
      .then((data) => {
        if (data) {
          setRenderStatus(data);
        }
      })
      .catch((err) => {
        // Handle connections wait/reboots during server deployments gracefully
        console.warn("Waiting for background render server to wake up...", err.message || err);
      });
  };

  const fetchYtStatus = () => {
    fetch(`/api/youtube/status/${video.id}`)
      .then((res) => {
        if (!res.ok) return null;
        return res.json();
      })
      .then((data) => {
        if (data) {
          setYtStatus(data);
        }
      })
      .catch((err) => {
        console.warn("Error fetching yt upload status:", err);
      });
  };

  useEffect(() => {
    fetchStatus();
    fetchYtStatus();

    // Set up smart, multi-stage continuous polling
    let intervalId: any = null;
    
    const isRenderDone = renderStatus?.status === "completed" || renderStatus?.status === "failed";
    const hasYtJobs = ytStatus?.jobs && ytStatus.jobs.length > 0;
    const isYtDone = hasYtJobs && ytStatus.jobs.every((j: any) => j.status === "completed" || j.status === "failed");
    
    // We keep polling if the render is not done, OR if YouTube jobs are not discovered yet, 
    // OR if YouTube jobs exist but are still pending or uploading.
    const shouldKeepPolling = !renderStatus || !isRenderDone || !hasYtJobs || !isYtDone;
    
    // Automatically query status every 4 seconds to maintain real-time feedback
    if (shouldKeepPolling) {
      intervalId = setInterval(() => {
        fetchStatus();
        fetchYtStatus();
      }, 4000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [video.id, renderStatus?.status, ytStatus?.jobs]);

  const handleQueueRender = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsQueueing(true);

    fetch("/api/render/save", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: video.id,
        metadata: {
          question: video.question,
          subject: video.subject,
          topic: video.topic,
          language: video.language,
          timestamp: video.timestamp,
          email: userEmail
        },
        videoData: video.videoData
      })
    })
      .then((res) => res.json())
      .then(() => {
        fetchStatus();
        setIsQueueing(false);
      })
      .catch((err) => {
        console.error("Queue render error:", err);
        setIsQueueing(false);
      });
  };

  // Helper properties to render progress nicely
  const isPending = renderStatus?.status === "pending";
  const isRendering = renderStatus?.status === "rendering";
  const isCompleted = renderStatus?.status === "completed";
  const isFailed = renderStatus?.status === "failed";
  const isNone = !renderStatus || renderStatus.status === "none";

  // Compute maximum current progress
  const progressRatio = isRendering 
    ? Math.floor(((renderStatus.progressLandscape || 0) + (renderStatus.progressShorts || 0)) / 2) 
    : 0;

  return (
    <motion.div
      layoutId={video.id}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      onClick={() => onReplayVideo(video.videoData, video.language)}
      className="bg-slate-900/40 backdrop-blur-md border border-white/5 rounded-3xl overflow-hidden shadow-lg hover:border-indigo-500/30 hover:shadow-[0_10px_30px_rgba(79,70,229,0.1)] group transition-all duration-300 flex flex-col cursor-pointer justify-between h-auto min-h-[420px] pb-5"
    >
      {/* Thumbnail Section */}
      <div className="relative aspect-video w-full overflow-hidden bg-slate-950 flex-shrink-0">
        {video.imageUrl ? (
          <>
            <img
              src={video.imageUrl}
              alt={video.title}
              referrerPolicy="no-referrer"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            {/* Accent Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent opacity-80" />
          </>
        ) : (
          <div className={cn("w-full h-full bg-gradient-to-br p-5 flex flex-col justify-between relative", getRandomGradient(video.subject))}>
            {/* Tech background math equations decoration */}
            <div className="absolute inset-0 opacity-[0.03] overflow-hidden pointer-events-none font-mono text-[9px] text-white select-none leading-relaxed">
              {"E = mc²\nf(x) = ∫ sin(x) dx\n∇ × B = μ₀J + μ₀ε₀ ∂E/∂t\nx = [-b ± √(b² - 4ac)] / 2a\nPV = nRT\nF = G(m₁m₂)/r²\nΨ(x,t) = Ae^(i(kx-wt))".split("\n").map((term, i) => (
                <div key={i} className="whitespace-nowrap" style={{ transform: `translate(${i * 20}px, ${i * 10}px)` }}>
                  {term}
                </div>
              ))}
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent opacity-60" />

            {/* Dynamic subject badge inside the abstract background */}
            <div className="relative z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 w-fit">
              <BookOpen className="w-3.5 h-3.5 text-indigo-400" />
              <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">{video.subject}</span>
            </div>

            {/* Central equation illustration */}
            <div className="relative z-10 flex flex-col justify-center items-center h-full">
              <span className="text-xl font-bold text-slate-300 font-serif italic drop-shadow">{video.topic}</span>
              <span className="text-[10px] text-slate-500 font-mono mt-1">Video Tutorial</span>
            </div>
          </div>
        )}

        {/* Video Badges & Indicators */}
        <div className="absolute top-3 left-3 z-10 flex gap-1.5 items-center">
          <span className="px-2.5 py-1 rounded-xl bg-slate-950/80 border border-white/10 text-slate-300 text-[10px] font-bold flex items-center gap-1">
            <Globe className="w-3 h-3 text-indigo-400" /> {video.language}
          </span>
        </div>

        {/* Play Hover Overlay */}
        <div className="absolute inset-0 bg-slate-950/40 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center z-10">
          <div className="w-12 h-12 rounded-full bg-indigo-500 flex items-center justify-center transform scale-75 group-hover:scale-100 transition-transform duration-300 shadow-[0_0_15px_rgba(99,102,241,0.5)]">
            <Play className="w-5 h-5 text-white fill-white ml-0.5" />
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="p-5 flex-grow flex flex-col justify-between">
        <div>
          {/* Upper line: Category, subject */}
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-[10px] font-semibold text-indigo-400 uppercase tracking-widest truncate max-w-[150px]">
              {video.topic || "General Concept"}
            </span>
            <span className="text-[9px] font-medium text-slate-500 font-mono flex items-center gap-1">
              <Clock className="w-3 h-3" /> {formatDate(video.timestamp).split(" ")[0]} {formatDate(video.timestamp).split(" ")[1]}
            </span>
          </div>

          {/* Title - Question */}
          <h4 className="text-sm font-bold text-slate-200 line-clamp-2 group-hover:text-white transition-colors leading-relaxed mb-3" title={video.question}>
            {video.question}
          </h4>
        </div>

        {/* BACKGROUND VIDEO EXPORT SYSTEM PIPELINE UI */}
        <div className="space-y-3 mt-1" onClick={(e) => e.stopPropagation()}>
          {isNone && (
            <button
              onClick={handleQueueRender}
              disabled={isQueueing}
              className="w-full py-2 px-3 rounded-xl bg-indigo-600/90 hover:bg-indigo-600 disabled:opacity-50 text-white font-extrabold text-[11px] tracking-wide text-center transition-all flex items-center justify-center gap-2 border border-indigo-500/30"
            >
              {isQueueing ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Preparing Assets...
                </>
              ) : (
                <>
                  <Film className="w-3.5 h-3.5" /> Compile MP4 Video exports
                </>
              )}
            </button>
          )}

          {isPending && (
            <div className="w-full py-2 px-3.5 rounded-xl bg-amber-500/5 text-amber-400 font-bold text-[11px] border border-amber-500/15 flex items-center gap-2 justify-center">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Enqueued for render pipeline...
            </div>
          )}

          {isRendering && (
            <div className="w-full space-y-2">
              <div className="flex justify-between text-[10px] font-black uppercase text-indigo-400">
                <span className="flex items-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin text-indigo-400" /> Exporting MP4
                </span>
                <span>{progressRatio}%</span>
              </div>
              <div className="w-full h-1.5 bg-slate-950 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-indigo-505 to-indigo-500 transition-all duration-300" style={{ width: `${progressRatio}%` }} />
              </div>
            </div>
          )}

          {isFailed && (
            <div className="w-full space-y-2">
              <div className="w-full py-2 px-3 rounded-xl bg-red-950/40 text-red-400 font-bold text-[11px] border border-red-900/30 flex items-center gap-1.5 justify-center">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" /> Compilation crashed.
              </div>
              <button
                onClick={handleQueueRender}
                className="w-full py-1.5 px-3 rounded-lg bg-slate-950 border border-slate-800 hover:bg-slate-900 text-slate-400 hover:text-white font-bold text-[10px]"
              >
                Retry Compile MP4
              </button>
            </div>
          )}

          {isCompleted && (
            <div className="flex flex-col gap-2.5">
              <div className="inline-flex items-center justify-between w-full text-[10px] font-black uppercase text-emerald-400">
                <span>MP4 COMPILED SUCCESSFULLY</span>
                <span className="bg-emerald-500/15 text-emerald-400 px-1.5 py-0.5 rounded text-[9px]">1080p</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <a
                  href={renderStatus.landscapeUrl}
                  download={`${video.topic || "Explanation"}_landscape.mp4`}
                  className="py-2 px-2 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/20 text-[10px] font-extrabold text-center transition-all flex items-center justify-center gap-1"
                >
                  <Download className="w-3 h-3" /> Landscape (16:9)
                </a>
                <a
                  href={renderStatus.shortsUrl}
                  download={`${video.topic || "Explanation"}_shorts.mp4`}
                  className="py-2 px-2 rounded-xl bg-pink-500/10 hover:bg-pink-500/20 text-pink-300 border border-pink-500/20 text-[10px] font-extrabold text-center transition-all flex items-center justify-center gap-1"
                >
                  <Download className="w-3 h-3" /> Shorts (9:16)
                </a>
              </div>

              {/* YouTube Uploads Section */}
              {ytStatus?.jobs && ytStatus.jobs.length > 0 && (
                <div className="mt-2.5 p-3 rounded-2xl bg-slate-950/80 border border-white/5 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-[10px] font-extrabold text-red-400 uppercase tracking-wider">
                      <Youtube className="w-3.5 h-3.5 text-red-500 fill-red-500 animate-pulse" /> YouTube Publishes
                    </span>
                    <span className="text-[9px] text-slate-500 font-mono">Automated IP</span>
                  </div>
                  <div className="space-y-2 text-[11px]">
                    {ytStatus.jobs.map((job: any) => {
                      const isPending = job.status === "pending";
                      const isUploading = job.status === "uploading";
                      const isCompleted = job.status === "completed";
                      const isFailed = job.status === "failed";
                      const progress = job.progress !== undefined ? job.progress : 0;
                      return (
                        <div key={job.id} className="py-1.5 border-b border-white/5 last:border-b-0 space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="text-slate-400 font-mono text-[10px] truncate max-w-[130px]" title={job.title}>
                              {job.type === "landscape" ? "📺 Landscape" : `📱 Short${job.partIndex ? ` (P${job.partIndex})` : ""}`}
                            </span>
                            <span className="font-bold text-[10px]">
                              {isPending && <span className="text-amber-500">Queued</span>}
                              {isUploading && (
                                <span className="text-indigo-400 flex items-center gap-1 animate-pulse">
                                  <Loader2 className="w-2.5 h-2.5 animate-spin" /> Uploading ({progress}%)
                                </span>
                              )}
                              {isCompleted && (
                                <a
                                  href={`https://youtube.com/watch?v=${job.youtubeVideoId}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-emerald-400 hover:underline flex items-center gap-0.5"
                                >
                                  Watch ↗
                                </a>
                              )}
                              {isFailed && (
                                <span className="text-red-450 text-red-400" title={job.error}>
                                  Failed
                                </span>
                              )}
                            </span>
                          </div>
                          
                          {/* Rich Upload Progress Bar */}
                          {isUploading && (
                            <div className="w-full h-1 bg-slate-950 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-red-500 to-indigo-500 transition-all duration-300" 
                                style={{ width: `${progress}%` }} 
                              />
                            </div>
                          )}

                          {/* Failure Error log details panel */}
                          {isFailed && job.error && (
                            <div className="space-y-1.5 mt-1">
                              <div className="text-[9px] text-red-400/80 font-mono leading-normal bg-red-950/20 px-2 py-1 rounded border border-red-900/20 max-w-full break-words">
                                {job.error}
                              </div>
                               {(job.error.toLowerCase().includes("authenticate") || job.error.toLowerCase().includes("reconnect")) && (
                                 <div className="space-y-1">
                                   <button
                                     onClick={(e) => {
                                       e.stopPropagation();
                                       connectOfficialYouTube(() => fetchYtStatus());
                                     }}
                                     className="w-full py-1.5 px-1.5 rounded-lg bg-red-600 hover:bg-red-500 text-white font-extrabold text-[9px] uppercase tracking-wider transition-all shadow-[0_0_8px_rgba(220,38,38,0.2)] flex items-center justify-center gap-1 mt-1 cursor-pointer"
                                   >
                                     <Youtube className="w-2.5 h-2.5 fill-white" /> Connect Official YouTube (@ProfessorXAI-108)
                                   </button>
                                   <button
                                     onClick={(e) => {
                                       e.stopPropagation();
                                       fetch("/api/youtube/config", {
                                         method: "POST",
                                         headers: { "Content-Type": "application/json" },
                                         body: JSON.stringify({ useSimulation: true })
                                       })
                                       .then(res => res.json())
                                       .then(() => {
                                         fetchYtStatus();
                                         if (typeof (window as any).checkYtConnection === "function") {
                                           (window as any).checkYtConnection();
                                         }
                                       })
                                       .catch(err => console.error("Error setting simulation:", err));
                                     }}
                                     className="w-full py-1.5 px-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-extrabold text-[9px] uppercase tracking-wider transition-all flex items-center justify-center gap-1 cursor-pointer"
                                     title="Bypass credentials check to complete successfully using a simulated upload"
                                   >
                                     ⚡ Switch to Simulation & Auto-Complete Upload
                                   </button>
                                 </div>
                               )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between pt-4 mt-3 border-t border-white/5 flex-shrink-0">
          <span className="text-xs text-slate-400 group-hover:text-indigo-300 font-bold transition-colors inline-flex items-center gap-1">
            Play Class <Play className="w-3 h-3 fill-current ml-0.5" />
          </span>

          <button
            onClick={(e) => handleDeleteVideo(e, video.id)}
            title="Delete from history"
            className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-all cursor-pointer active:scale-90"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

